import React, { useState } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import AlertModal from './AlertModal';

const AddCellModal = ({
  quotaProjectId,
  dimensions,
  cells = [], // Existing cells to calculate allocated quotas
  cell,
  cloneFrom,
  contextFilters,
  pendingModifications = [], // Pending cell modifications (to use pending targets)
  onClose,
  onSuccess
}) => {
  // Editable form: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(onClose, { backdropClose: false });

  // If cloneFrom is provided, use its values for cloning
  const sourceCell = cloneFrom || cell;
  
  const [dimensionValues, setDimensionValues] = useState(
    sourceCell?.dimension_values_json || contextFilters || {}
  );
  const [targetCount, setTargetCount] = useState(sourceCell?.target_count || 0);
  const [notes, setNotes] = useState(cloneFrom ? '' : (sourceCell?.notes || ''));
  const [loading, setLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });

  // Calculate allocated quota for each dimension value
  // Uses pending target values when available (so pending decreases free up quota)
  const getAllocatedQuota = (dimensionId, valueCode) => {
    return cells
      .filter(c => {
        // Exclude current cell if editing
        if (cell && c.id === cell.id) return false;
        const cellValues = c.dimension_values_json || {};
        return cellValues[dimensionId] === valueCode;
      })
      .reduce((sum, c) => {
        // Pending deletion = count as 0
        const hasPendingDelete = pendingModifications.some(
          pm => Number(pm.quota_cell_id) === Number(c.id) && pm.field === 'delete'
        );
        if (hasPendingDelete) return sum;
        // Pending target change = use new value
        const pendingMod = pendingModifications.find(
          pm => Number(pm.quota_cell_id) === Number(c.id) && pm.field === 'target_count'
        );
        const effectiveTarget = pendingMod ? (parseInt(pendingMod.new_value) || 0) : (c.target_count || 0);
        return sum + effectiveTarget;
      }, 0);
  };

  // Get available quota for a dimension value (target_quota - allocated)
  const getAvailableQuota = (dimensionId, valueCode) => {
    const dimension = dimensions.find(d => d.id === dimensionId);
    if (!dimension) return 0;
    
    const value = dimension.values_json?.find(v => v.code === valueCode);
    if (!value) return 0;
    
    const targetQuota = parseInt(value.target_quota) || 0;
    const allocated = getAllocatedQuota(dimensionId, valueCode);
    
    return Math.max(0, targetQuota - allocated);
  };

  // Get total available quota for current cell combination
  const getMaxAvailableQuota = () => {
    let maxQuota = Infinity;
    
    Object.entries(dimensionValues).forEach(([dimId, valueCode]) => {
      if (valueCode) {
        const available = getAvailableQuota(parseInt(dimId), valueCode);
        maxQuota = Math.min(maxQuota, available);
      }
    });
    
    return maxQuota === Infinity ? 0 : maxQuota;
  };

  const handleDimensionValueChange = (dimensionId, valueCode) => {
    setDimensionValues(prev => ({
      ...prev,
      [dimensionId]: valueCode
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required dimensions
    const requiredDimensions = dimensions.filter(d => d.is_required);
    const missingRequired = requiredDimensions.filter(
      dim => !dimensionValues[dim.id]
    );

    if (missingRequired.length > 0) {
      setAlertModal({
        show: true,
        title: 'Champs requis',
        message: `Veuillez sélectionner des valeurs pour: ${missingRequired.map(d => d.label).join(', ')}`,
        type: 'warning'
      });
      return;
    }

    if (targetCount < 0) {
      setAlertModal({
        show: true,
        title: 'Valeur invalide',
        message: 'Le nombre cible doit être positif',
        type: 'warning'
      });
      return;
    }

    // Validate that selected values have quota > 0
    const invalidValues = [];
    Object.entries(dimensionValues).forEach(([dimId, valueCode]) => {
      if (valueCode) {
        const dimension = dimensions.find(d => d.id === parseInt(dimId));
        if (dimension) {
          const value = dimension.values_json?.find(v => v.code === valueCode);
          if (value && (parseInt(value.target_quota) || 0) === 0) {
            invalidValues.push(`${dimension.label}: ${value.label}`);
          }
        }
      }
    });

    if (invalidValues.length > 0) {
      setAlertModal({
        show: true,
        title: 'Valeurs invalides',
        message: `Les valeurs suivantes ont un quota de 0 et ne peuvent pas être utilisées: ${invalidValues.join(', ')}`,
        type: 'error'
      });
      return;
    }

    // Validate quota limits for each dimension value
    const quotaExceeded = [];
    const newTargetCount = parseInt(targetCount) || 0;
    
    Object.entries(dimensionValues).forEach(([dimId, valueCode]) => {
      if (valueCode) {
        const available = getAvailableQuota(parseInt(dimId), valueCode);
        if (newTargetCount > available) {
          const dimension = dimensions.find(d => d.id === parseInt(dimId));
          const value = dimension?.values_json?.find(v => v.code === valueCode);
          quotaExceeded.push({
            dimension: dimension?.label || dimId,
            value: value?.label || valueCode,
            available,
            requested: newTargetCount
          });
        }
      }
    });

    if (quotaExceeded.length > 0) {
      const messages = quotaExceeded.map(q => 
        `${q.dimension} - ${q.value}: quota disponible ${q.available}, demandé ${q.requested}`
      );
      setAlertModal({
        show: true,
        title: 'Quota dépassé',
        message: `Le quota demandé dépasse le quota disponible pour:\n${messages.join('\n')}`,
        type: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Ensure all required dimensions are included in payload
      const finalDimensionValues = { ...dimensionValues };
      dimensions.forEach(dim => {
        if (dim.is_required && !(dim.id in finalDimensionValues)) {
          // This shouldn't happen due to validation above, but ensure it's included
          finalDimensionValues[dim.id] = '';
        }
      });
      
      console.log('Saving cell with dimension values:', finalDimensionValues);
      
      const payload = {
        dimension_values_json: finalDimensionValues,
        target_count: parseInt(targetCount),
        notes: notes || null,
        status: cell?.status || 'pending'
      };

      let response;
      if (cell) {
        response = await axios.put(
          `/quotas/projects/${quotaProjectId}/cells/${cell.id}`,
          payload
        );
      } else {
        response = await axios.post(
          `/quotas/projects/${quotaProjectId}/cells`,
          payload
        );
      }

      const cellData = response.data.data;
      if (response.data.pending_creation) {
        cellData._pendingCreation = true;
      }
      onSuccess(cellData);
    } catch (error) {
      console.error('Error saving cell:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la sauvegarde',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" {...dismiss}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {cloneFrom ? 'Dupliquer la cellule' : cell ? 'Modifier la cellule' : 'Ajouter une cellule'}
          </h2>
          <button className="btn-icon" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Valeurs de dimensions</label>
            {dimensions.map(dimension => {
              const selectedValue = dimensionValues[dimension.id];
              const values = dimension.values_json || [];

              return (
                <div key={dimension.id} className="dimension-select">
                  <label>
                    {dimension.label}
                    {dimension.is_required && <span className="required">*</span>}
                  </label>
                  <select
                    value={selectedValue || ''}
                    onChange={(e) => handleDimensionValueChange(dimension.id, e.target.value)}
                    required={dimension.is_required}
                  >
                    <option value="">-- Sélectionner --</option>
                    {values
                      .filter(v => {
                        // Filter out inactive values
                        if (v.is_active === false) return false;
                        // Filter out values with target_quota = 0
                        const targetQuota = parseInt(v.target_quota) || 0;
                        return targetQuota > 0;
                      })
                      .map(value => {
                        const available = getAvailableQuota(dimension.id, value.code);
                        const targetQuota = parseInt(value.target_quota) || 0;
                        const allocated = targetQuota - available;
                        const label = available > 0 
                          ? `${value.label} (${allocated}/${targetQuota} utilisé, ${available} disponible)`
                          : `${value.label} (${targetQuota} utilisé, 0 disponible)`;
                        
                        return (
                          <option 
                            key={value.code} 
                            value={value.code}
                            disabled={available === 0}
                          >
                            {label}
                          </option>
                        );
                      })}
                  </select>
                </div>
              );
            })}
          </div>

          <div className="form-group">
            <label>Nombre cible *</label>
            <input
              type="number"
              value={targetCount}
              onChange={(e) => setTargetCount(e.target.value)}
              min="0"
              max={getMaxAvailableQuota()}
              required
            />
            {Object.keys(dimensionValues).length > 0 && (
              <small className="form-text text-muted">
                Quota maximum disponible: {getMaxAvailableQuota()}
              </small>
            )}
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              placeholder="Notes optionnelles..."
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (cloneFrom ? 'Création...' : cell ? 'Sauvegarde...' : 'Création...') : (cloneFrom ? 'Créer la copie' : cell ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </form>
      </div>

      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />
    </div>
  );
};

export default AddCellModal;

